/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Pagination,
  Table,
  Typography,
  UtilityButton,
  useTheme,
  Option,
  Icon,
  ToggleButtonGroup,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { MerchantSelect, QBLoader } from 'components';
import { getSettlementReports } from 'tabs/Accounting/api';
const PAGE_SIZE = 16;

const HEADERS = [
  { label: 'Timespan', cellKey: 'timespan', sort: false },
  { label: 'Id', cellKey: 'uuid', sort: false },
  { label: 'Merchant', cellKey: 'merchant', sort: false },
  { label: 'Processing Fee', cellKey: 'processingFee', sort: false },
  { label: 'Total sales volume', cellKey: 'totalSalesVolume', sort: false },
  { label: 'Total', cellKey: 'total', sort: false },
  { label: 'Balance', cellKey: 'balance', sort: false },
  { label: 'Processed', cellKey: 'processed', sort: false },
];

interface SettlementOrderFilters {
  page: number;
  merchant: Option[];
  processed: boolean;
  processingMerchant: boolean;
}

const initialFilterState: SettlementOrderFilters = {
  page: 1,
  merchant: [],
  processed: true,
  processingMerchant: true,
};

const getInitialFilterState = (
  params: URLSearchParams
): SettlementOrderFilters => {
  return {
    page: Number(params.get('page')) || 1,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    merchant: params.get('merchant')
      ? JSON.parse(params.get('merchant') as string)
      : [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    processed: params.get('processed')
      ? JSON.parse(params.get('processed') as string)
      : true,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    processingMerchant: params.get('processingMerchant')
      ? JSON.parse(params.get('processingMerchant') as string)
      : true,
  };
};

export const History = () => {
  const { spacing, palette } = useTheme();
  const [params, setParams] = useSearchParams();
  const [filterState, setFilterState] = useState<SettlementOrderFilters>(
    getInitialFilterState(params)
  );
  const { page, merchant, processed, processingMerchant } = filterState;
  const setFilterStateFor = <K extends keyof SettlementOrderFilters>(
    filterName: K,
    isObject: boolean
  ) => {
    return (newValue: SettlementOrderFilters[K]) => {
      params.set(
        filterName,
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        isObject ? JSON.stringify(newValue) : String(newValue)
      );
      setParams(params);
      setFilterState((state) => ({ ...state, [filterName]: newValue }));
    };
  };
  const setPage = setFilterStateFor('page', false);
  const setMerchant = setFilterStateFor('merchant', true);
  const setProcessed = setFilterStateFor('processed', true);
  const setProcessingMerchant = setFilterStateFor('processingMerchant', true);

  const [{ value, loading }, refresh] = useAsyncFn(
    () =>
      getSettlementReports({
        pagination: { size: PAGE_SIZE, page },
        filters: {
          merchant_ids: merchant.map((m) => m.value),
          processed,
          processing_merchant: processingMerchant,
        },
      }),
    [page, merchant, processed, processingMerchant]
  );

  const handleResetFilters = () => {
    setParams(undefined);
    setFilterState(initialFilterState);
  };

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rows = useMemo(
    () =>
      value?.reports.map((report) => {
        const totalSettlementAmount = (
          report.adjustments.reduce(
            (acc, curr) => acc + curr.amount,
            report.settlementAmount
          ) + (report.refundDebt ? report.refundDebt : 0)
        ).toFixed(2);
        return {
          value: {
            timespan: `${report.startTime.toLocaleDateString()} - ${report.endTime.toLocaleDateString()}`,
            uuid: <TruncatedString text={report.uuid} />,
            merchant: report.merchant.name,
            processingFee: report.processingFee,
            totalSalesVolume: report.totalAmount,
            total: totalSettlementAmount,
            balance: report.amountRemainingToBePaid.toFixed(2),
            processed: report.processed,
            id: report.uuid,
          },
        };
      }),
    [value]
  );

  if (!value) return <QBLoader />;
  return (
    <>
      <Header>
        {' '}
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <Typography variant="subtitle">History</Typography>
          </div>
          <UtilityButton text="Refresh" onClick={refresh} variant="secondary" />
        </div>
      </Header>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'repeat(5, auto) 1fr',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <ToggleButtonGroup
          toggleOptions={[
            { label: 'Processing', value: 'PROCESSING' },
            { label: 'Others', value: '' },
          ]}
          onChange={() => {
            setProcessingMerchant(!processingMerchant);
            setMerchant([]);
            setPage(1);
          }}
          selected={processingMerchant ? 'PROCESSING' : ''}
        />
        <MerchantSelect
          merchant={merchant}
          setMerchant={(m) => {
            setPage(1);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            setMerchant((m as Option[]) || []);
          }}
          searchable={true}
          multiple={true}
        />
        <ToggleButtonGroup
          toggleOptions={[
            { label: 'Processed', value: 'PROCESSED' },
            { label: 'Unprocessed', value: 'UNPROCESSED' },
          ]}
          onChange={() => {
            setProcessed(!processed);
            setPage(1);
          }}
          selected={processed ? 'PROCESSED' : 'UNPROCESSED'}
        />
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={refresh}
          disabled={loading}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>
      <>
        <div css={{ marginTop: spacing.xxs }}>
          <Table
            headers={HEADERS}
            rows={rows ?? []}
            onRowClick={(row) => {
              // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
              window.open(`/accounting/reports/${row.id}`, '_blank');
            }}
          />
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Pagination
            currentPage={page}
            onChange={setPage}
            numberOfElements={value.aggregatedData.count}
            pageSize={PAGE_SIZE}
          />
        </div>
      </>
    </>
  );
};
